import React, { useState } from 'react';
import { Link, ListItem, UnorderedList, VStack } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LegalSection, LegalShell, P, Item, TermsNotice } from './LegalShell';
import { readLang, writeLang } from './siteLang';

/* ============================================================
   英文版：面向美国主体的 Terms and Conditions（原文，未改动）
   ============================================================ */
const EnBody = () => (
  <>
    <P>
      Aiker Inc. ("Aiker", "we," "us," or "our") provides a natural language processing platform that
      allows consumers to interact with our customers via an AI-based phone service (the "Service"),
      including through our website at https://bizsprite.com (the "Site"), related mobile
      applications, and the AI‑powered phone services we operate, subject to the following Terms and
      Conditions ("T&amp;C"). By registering for or creating an Aiker account, or accessing,
      browsing, or otherwise using the Site or Service, you acknowledge that you have read,
      understood, and agree to be bound by these T&amp;C.
    </P>
    <P>
      Aiker may change these T&amp;C from time to time by providing thirty (30) days prior notice
      either by emailing the email address associated with your account or by posting a notice on the
      Site. If any change is not acceptable to you, your only remedy is to stop using the Services
      and send a cancellation email to{' '}
      <Link href="mailto:support@bizsprite.com" color="blue.600">support@bizsprite.com</Link>.
    </P>

    <LegalSection title="Access and Use of the Service">
      <VStack align="start" spacing={3}>
        <Item label="Service Description:">
          The Service is designed to provide a natural language processing platform that allows
          consumers to interact with our customers via an AI-based phone service. The Service supports
          multiple languages, including English, Mandarin Chinese, Spanish, and Tagalog, and is
          intended for use by small and medium-sized businesses.
        </Item>
        <Item label="Registration Obligations:">
          You must provide accurate registration information. If you are under 13 years of age, you
          are not authorized to use the Service. If you are under 18, you may use the Service only
          with approval of your parent or guardian.
        </Item>
        <Item label="Account Security:">
          You are responsible for maintaining the confidentiality of your password and account. You
          agree to immediately notify Aiker of any unauthorized use of your account.
        </Item>
        <Item label="Modifications to Service:">
          Aiker reserves the right to modify or discontinue the Service (or any part thereof) with or
          without notice.
        </Item>
      </VStack>
    </LegalSection>

    <LegalSection title="Conditions of Access and Use">
      <P mb={3}>
        <Item label="User Conduct:">
          You are solely responsible for all content you make available through the Service. You agree{' '}
          <strong>not</strong> to use the Service to:
        </Item>
      </P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>Upload content that infringes any intellectual property or other proprietary rights;</ListItem>
        <ListItem>Upload content that contains software viruses or code designed to disrupt or damage computer systems;</ListItem>
        <ListItem>Interfere with or disrupt the Service or servers or networks connected to the Service;</ListItem>
        <ListItem>Violate any applicable local, state, national, or international law, including laws against recording users without consent;</ListItem>
        <ListItem>Impersonate any person or entity;</ListItem>
        <ListItem>Solicit personal information from anyone under the age of 18;</ListItem>
        <ListItem>Engage in data mining, robots, scraping, or similar extraction methods;</ListItem>
        <ListItem>Circumvent any blocking measures implemented by Aiker.</ListItem>
      </UnorderedList>
      <P mt={3}>
        <Item label="Competitors:">
          No employee, independent contractor, agent, or affiliate of any competing natural language
          processing or AI-based company is permitted to view, access, or use any portion of the
          Service without express written permission from Aiker.
        </Item>
      </P>
    </LegalSection>

    <LegalSection title="Fees, Payments & Cancellations">
      <VStack align="start" spacing={3}>
        <Item label="Fees and Billing:">
          If you purchase our Services, you must select a payment plan and provide accurate payment
          information. You authorize Aiker (through Stripe) to charge your payment method on a
          recurring monthly basis until you cancel. All fees are non‑refundable except as expressly
          set forth herein.
        </Item>
        <Item label="Price Changes:">
          We may change our fees at any time by providing at least thirty (30) days' notice. Continued
          use after the change constitutes acceptance.
        </Item>
        <Item label="Cancellation:">
          You may cancel your subscription at any time through your account dashboard. Cancellation
          takes effect at the end of your current monthly billing period. No refunds for partial months.
        </Item>
        <Item label="Refunds:">
          All payments are final. Aiker does not offer refunds for any unused portion of a subscription
          period.
        </Item>
      </VStack>
    </LegalSection>

    <LegalSection title="Intellectual Property Rights">
      <VStack align="start" spacing={3}>
        <Item label="Service Content:">
          The Service may contain content protected by copyright, patent, trademark, or other
          proprietary rights. Except as expressly authorized, you agree not to copy, modify, scrape,
          sell, or create derivative works based on the Service Content.
        </Item>
        <Item label="Call Recordings:">
          As between you and Aiker, Aiker owns all call recordings, transcripts, and related data
          generated through the Service. You grant Aiker a non‑exclusive, worldwide, royalty‑free,
          perpetual license to use, reproduce, and analyze User Content and Customer Information to
          provide, improve, and develop the Service, including for training our natural language models.
        </Item>
        <Item label="Trademarks:">
          The Aiker name and logos are trademarks of Aiker. Nothing in these T&amp;C grants any license
          or right to use Aiker Trademarks without our prior written permission.
        </Item>
      </VStack>
    </LegalSection>

    <LegalSection title="Copyright Complaints">
      <P mb={3}>
        Aiker respects the intellectual property of others. If you believe your work has been copied in
        a way that constitutes copyright infringement, notify our Copyright Agent by mail at:
        168 W Pomona Av, Monrovia, CA 91016.
      </P>
      <P>
        Your notification must include: (1) a physical or electronic signature of the rights owner;
        (2) identification of the infringed work; (3) identification of the infringing content and its
        location; (4) your contact information; (5) a good faith belief statement; and (6) a statement
        of accuracy under penalty of perjury.
      </P>
    </LegalSection>

    <LegalSection title="Third-Party Services">
      <P>
        The Service may provide links or access to Third-Party Services. Aiker has no control over and
        is not responsible for the accuracy, availability, or privacy practices of Third-Party
        Services. Your dealings with third parties while using the Service are between you and the
        third party.
      </P>
    </LegalSection>

    <LegalSection title="Indemnification">
      <P>
        You agree to defend, indemnify, and hold harmless Aiker, its affiliates, officers, employees,
        directors, and agents from any and all losses, damages, expenses (including attorneys' fees),
        and claims arising out of or relating to your use of the Service, any User Content, your
        connection to the Service, or your violation of these T&amp;C or any rights of another.
      </P>
    </LegalSection>

    <LegalSection title="Disclaimer of Warranties">
      <P fontSize="sm">
        YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS
        AVAILABLE" BASIS. THE AIKER PARTIES EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER
        EXPRESS, IMPLIED OR STATUTORY, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
        FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. YOU ACKNOWLEDGE THAT CERTAIN COMPONENTS
        OF THE SERVICE ARE BASED ON PREDICTIVE STATISTICAL MODELS AND MAY CONTAIN BUGS, MAKE ERRORS,
        OR MISINTERPRET REQUESTS.
      </P>
    </LegalSection>

    <LegalSection title="Limitation of Liability">
      <P fontSize="sm">
        THE AIKER PARTIES WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
        EXEMPLARY DAMAGES, INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, OR DATA, WHETHER
        BASED ON CONTRACT, TORT, NEGLIGENCE, OR OTHERWISE. IN NO EVENT WILL THE AIKER PARTIES' TOTAL
        LIABILITY TO YOU FOR ALL DAMAGES EXCEED THE AMOUNT YOU HAVE PAID AIKER IN THE LAST SIX (6)
        MONTHS, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
      </P>
    </LegalSection>

    <LegalSection title="Dispute Resolution by Binding Arbitration">
      <P color="gray.600" fontSize="sm" fontStyle="italic" mb={3}>
        Please read this section carefully as it affects your rights.
      </P>
      <VStack align="start" spacing={3}>
        <Item label="a. Agreement to Arbitrate:">
          Any and all disputes or claims that have arisen or may arise between you and Aiker will be
          resolved exclusively through final and binding arbitration, rather than a court, except that
          you may assert individual claims in small claims court. You and Aiker each waive the right to
          a trial by jury or to participate in a class action.
        </Item>
        <P fontSize="sm">
          <Item label="b. No Class Actions:">
            YOU AND AIKER AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY ON AN INDIVIDUAL
            BASIS AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION.
          </Item>
        </P>
        <Item label="c. Pre-Arbitration Dispute Resolution:">
          Most customer concerns can be resolved quickly by emailing customer support. If efforts
          prove unsuccessful, a party intending to seek arbitration must first send a written Notice
          of Dispute by certified mail to: Aiker Inc, 168 W Pomona Av, Monrovia, CA 91016.
        </Item>
        <Item label="d. Arbitration Procedures:">
          Arbitration will be conducted by a neutral arbitrator in accordance with the American
          Arbitration Association's ("AAA") rules. For information, visit{' '}
          <Link href="http://www.adr.org" color="blue.600" isExternal>www.adr.org</Link>.
        </Item>
      </VStack>
    </LegalSection>

    <LegalSection title="Termination">
      <P>
        Aiker may, in its sole discretion, suspend or terminate your account or use of the Service for
        any reason, including for lack of use or violation of these T&amp;C. Aiker may also discontinue
        providing the Service, or any part thereof, with or without notice.
      </P>
    </LegalSection>

    <LegalSection title="General">
      <VStack align="start" spacing={3}>
        <P>
          These T&amp;C constitute the entire agreement between you and Aiker governing your access and
          use of the Service, and supersede any prior agreements. These T&amp;C will be governed by the
          laws of the State of California without regard to its conflict of law provisions.
        </P>
        <P>
          With respect to any disputes not subject to arbitration, you and Aiker submit to the personal
          and exclusive jurisdiction of the state and federal courts located within Los Angeles,
          California.
        </P>
        <P>
          Any claim or cause of action arising out of or related to use of the Service or these
          T&amp;C must be filed within one (1) year after such claim or cause of action arose or be
          forever barred.
        </P>
      </VStack>
    </LegalSection>

    <LegalSection title="Notice for California Users">
      <P>
        Under California Civil Code Section 1789.3, California consumers may contact the Complaint
        Assistance Unit of the Division of Consumer Services of the California Department of Consumer
        Affairs at <Link href="mailto:dca@dca.ca.gov" color="blue.600">dca@dca.ca.gov</Link> or
        (800) 952-5210. You may also contact us at Aiker Inc, 168 W Pomona Av, Monrovia, CA 91016;
        Attn: Legal &amp; Compliance Dept.
      </P>
    </LegalSection>

    <LegalSection title="Questions? Concerns? Suggestions?">
      <P>
        Please contact us at{' '}
        <Link href="mailto:support@bizsprite.com" color="blue.600">support@bizsprite.com</Link> or by writing
        to Aiker Inc, 168 W Pomona Av, Monrovia, CA 91016 to report any violations of these T&amp;C or
        to pose any questions.
      </P>
    </LegalSection>
  </>
);

/* ============================================================
   中文版：官方《Bizsprite AI 语音客服业务服务条款》
   与英文版适用范围不同（英文版面向美国主体，含仲裁条款），
   所以不是翻译，而是两份各自生效的文档。
   ============================================================ */
const ZhBody = () => (
  <>
    <P>
      Bizsprite 为企业客户提供 AI 语音客服相关服务。本服务条款适用于客户开通、使用 Bizsprite 服务及账户管理的全过程。
      具体服务内容以客户所购买套餐、订单页面、服务确认单或双方书面约定为准。
    </P>

    <LegalSection title="一、服务说明">
      <P mb={3}>Bizsprite 为企业客户提供 AI 语音客服相关服务，包括但不限于：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>企业专属电话号码申请及配置；</ListItem>
        <ListItem>AI 来电接听服务；</ListItem>
        <ListItem>企业信息、产品资料、FAQ 知识库导入；</ListItem>
        <ListItem>AI 基于客户提供资料进行自动问答；</ListItem>
        <ListItem>通话记录、客户问题及服务数据整理；</ListItem>
        <ListItem>根据业务需要提供基础配置、使用指导及售后支持。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="二、固定月费与 AI 使用说明">
      <P mb={3}>
        Bizsprite 采用固定月费套餐模式。在客户正常商业客服使用范围内，Bizsprite 不按照 AI Token
        用量单独向客户收取费用，也不会因客户在套餐内正常使用 AI 对话功能而额外计收 AI 使用量费用。
      </P>
      <P mb={3}>客户理解并同意：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>「不按 Token 单独计费」不等同于服务资源无限制使用；</ListItem>
        <ListItem>服务使用应符合正常商业客服场景；</ListItem>
        <ListItem>
          Bizsprite 有权依据合理使用政策，对异常高频、非正常业务用途、恶意调用、攻击流量或明显超出正常业务范围的使用行为进行限制、调整或另行收费；
        </ListItem>
        <ListItem>
          如客户业务规模明显增长，且实际资源占用长期超出当前套餐合理范围，Bizsprite 有权建议客户升级套餐或另行协商企业级服务方案。
        </ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="三、合理使用政策">
      <P mb={3}>为保障所有客户的服务稳定性和公平性，客户应在合理商业范围内使用本服务。以下情形不属于正常商业客服使用范围：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>非真实客户服务目的的大量拨测、压测或重复测试；</ListItem>
        <ListItem>使用自动化程序、脚本、机器人等方式持续触发 AI 对话；</ListItem>
        <ListItem>将服务用于呼叫中心外包、转售、批量号码接入或第三方平台调用；</ListItem>
        <ListItem>短时间内产生异常高频来电、异常长时间通话或异常 AI 交互；</ListItem>
        <ListItem>利用服务进行骚扰、诈骗、虚假营销、违法宣传或其他违规用途；</ListItem>
        <ListItem>因客户广告投放、活动推广或其他原因造成流量激增，但未提前通知 Bizsprite 进行资源准备；</ListItem>
        <ListItem>明显超出所购套餐面向场景的其他异常使用行为。</ListItem>
      </UnorderedList>
      <P mt={3} mb={3}>如出现上述情况，Bizsprite 有权采取以下措施：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>提醒客户调整使用方式；</ListItem>
        <ListItem>临时限制部分功能或并发接听能力；</ListItem>
        <ListItem>要求客户升级套餐或购买额外资源；</ListItem>
        <ListItem>对异常产生的第三方通信资源费用、短信费用、人工处理费用或定制服务费用另行计费；</ListItem>
        <ListItem>在严重违规或存在违法风险时暂停或终止服务。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="四、来电接听与通话资源说明">
      <P mb={3}>客户购买套餐后，可在套餐约定范围内使用 AI 来电接听服务。</P>
      <P mb={3}>
        Bizsprite 对外宣传中提及的「固定月费」「不按 AI Token 单独计费」「套餐内来电接听」等内容，
        均指正常商业客服使用场景下的服务权益。
      </P>
      <P mb={3}>以下费用不一定包含在基础套餐内，具体以双方订单或服务确认单为准：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>电话号码申请费、号码月租费；</ListItem>
        <ListItem>短信接收或发送费用；</ListItem>
        <ListItem>国际通话、特殊线路或第三方通信资源费用；</ListItem>
        <ListItem>超出套餐约定范围的高频通话、长时通话或大规模并发接听；</ListItem>
        <ListItem>人工客服、定制开发、API 对接、数据清洗、知识库搭建等额外服务。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="五、客户资料与知识库责任">
      <P mb={3}>
        客户应向 Bizsprite 提供真实、准确、合法、完整的企业信息、产品资料、FAQ、售后政策、配送政策、价格信息及其他必要资料。
      </P>
      <P mb={3}>客户理解并同意：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>AI 回复效果依赖于客户提供资料的完整性和准确性；</ListItem>
        <ListItem>如客户资料错误、过期、不完整，由此导致的 AI 回复偏差、客户误解或业务损失，由客户自行承担；</ListItem>
        <ListItem>客户应及时更新产品、价格、库存、物流、售后政策等信息；</ListItem>
        <ListItem>Bizsprite 可协助客户优化知识库，但不对客户业务政策本身承担审核责任。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="六、AI 回复与服务边界">
      <P mb={3}>Bizsprite AI 语音客服用于辅助企业完成客户咨询、基础问答、信息收集及服务引导。客户理解并同意：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>AI 回复不应被视为法律、财务、医疗、投资或其他专业意见；</ListItem>
        <ListItem>对于退款、赔偿、合同承诺、价格变更、重大投诉等高风险事项，客户应设置人工复核机制；</ListItem>
        <ListItem>AI 可能因语音识别、客户表达、背景噪音、知识库内容或系统限制产生误解或不完整回复；</ListItem>
        <ListItem>Bizsprite 将持续优化服务效果，但不承诺 AI 回复在所有场景下完全准确、完整或无误；</ListItem>
        <ListItem>客户应根据自身业务情况，对 AI 可回答范围、禁答内容和转人工规则进行设置。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="七、服务开通与交付">
      <P mb={3}>客户完成付款并提交必要资料后，Bizsprite 将根据实际情况启动服务配置。标准开通流程包括：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>客户提交企业信息及服务申请；</ListItem>
        <ListItem>配置电话号码或通信资源；</ListItem>
        <ListItem>创建 AI Agent；</ListItem>
        <ListItem>导入企业资料、产品信息及 FAQ；</ListItem>
        <ListItem>进行基础测试；</ListItem>
        <ListItem>激活服务并投入使用。</ListItem>
      </UnorderedList>
      <P mt={3}>
        实际开通时间可能受到客户资料完整度、号码申请进度、第三方通信服务商审核、系统配置复杂度等因素影响。
      </P>
    </LegalSection>

    <LegalSection title="八、服务稳定性与第三方资源">
      <P mb={3}>
        Bizsprite 将尽合理努力保障服务稳定运行。客户理解并同意，Bizsprite
        服务可能依赖第三方通信运营商、云服务商、AI 模型服务商、网络服务商及其他技术服务提供方。
      </P>
      <P mb={3}>因以下原因导致服务中断、延迟、失败或异常的，Bizsprite 不承担超出合理范围的赔偿责任：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>第三方通信线路、电话号码资源或运营商服务异常；</ListItem>
        <ListItem>云服务、AI 模型接口或网络基础设施故障；</ListItem>
        <ListItem>客户自身网络、设备、账号或配置问题；</ListItem>
        <ListItem>不可抗力、政策变化、监管要求或安全事件；</ListItem>
        <ListItem>客户违规使用、异常流量或攻击行为导致的服务限制。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="九、费用支付与续费">
      <P mb={3}>客户应按照所选套餐按月、按年或双方约定方式支付服务费用。</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>服务费用以订单、报价单或服务确认单为准；</ListItem>
        <ListItem>客户未按时付款的，Bizsprite 有权暂停服务；</ListItem>
        <ListItem>服务到期后，如客户未续费，Bizsprite 有权停止 AI 接听、号码管理、数据查询等相关服务；</ListItem>
        <ListItem>已开通服务后，除双方另有约定外，已支付费用不因客户未使用、少量使用或单方面停止使用而自动退还；</ListItem>
        <ListItem>涉及号码、通信资源、定制开发、人工服务等已实际发生的成本，不支持退还。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="十、数据与隐私">
      <P mb={3}>
        Bizsprite 将根据服务需要处理客户提交的企业资料、知识库内容、通话记录、客户问题、通话文本及相关服务数据。Bizsprite 承诺：
      </P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>不会将客户业务数据用于与服务无关的非法用途；</ListItem>
        <ListItem>不会主动向无关第三方出售客户数据；</ListItem>
        <ListItem>将采取合理技术和管理措施保护客户数据安全；</ListItem>
        <ListItem>在法律法规、监管要求或司法机关依法要求时，Bizsprite 可能需要配合提供相关信息。</ListItem>
      </UnorderedList>
      <P mt={3}>客户应确保其收集、上传、使用客户信息和通话数据的行为符合适用法律法规及平台规则。</P>
    </LegalSection>

    <LegalSection title="十一、禁止用途">
      <P mb={3}>客户不得将 Bizsprite 服务用于以下用途：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>诈骗、骚扰、虚假宣传、非法营销；</ListItem>
        <ListItem>违反电信、隐私、消费者保护、广告、数据安全等相关法律法规的行为；</ListItem>
        <ListItem>侵犯他人知识产权、隐私权、名誉权或其他合法权益；</ListItem>
        <ListItem>成人、赌博、毒品、武器、非法金融、仿冒商品等高风险或违法业务；</ListItem>
        <ListItem>规避平台审核、虚假注册、批量账号操作或其他违反第三方平台规则的行为；</ListItem>
        <ListItem>任何可能损害 Bizsprite、通信服务商、其他客户或公众利益的行为。</ListItem>
      </UnorderedList>
      <P mt={3}>如客户违反本条款，Bizsprite 有权立即暂停或终止服务，并保留追究责任的权利。</P>
    </LegalSection>

    <LegalSection title="十二、套餐调整与服务变更">
      <P>
        Bizsprite 有权根据产品升级、成本变化、第三方资源变化或业务调整，对套餐内容、价格、功能范围、合理使用标准进行调整。
        对于已付费客户，Bizsprite 将尽合理努力提前通知重大调整。客户可在当前服务周期结束后选择续费、升级、降级或停止使用服务。
      </P>
    </LegalSection>

    <LegalSection title="十三、责任限制">
      <P mb={3}>
        在法律允许的范围内，Bizsprite 对因使用或无法使用本服务导致的间接损失、预期利润损失、客户流失、订单损失、商誉损失或第三方索赔不承担责任。
      </P>
      <P>
        如因 Bizsprite 原因造成客户直接损失，Bizsprite 的累计赔偿责任原则上不超过客户最近一个月已支付的基础服务费用，双方另有书面约定的除外。
      </P>
    </LegalSection>

    <LegalSection title="十四、服务暂停与终止">
      <P mb={3}>出现以下情况时，Bizsprite 有权暂停或终止服务：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>客户逾期未支付服务费用；</ListItem>
        <ListItem>客户违反本服务条款或合理使用政策；</ListItem>
        <ListItem>客户业务存在违法、违规或高风险情形；</ListItem>
        <ListItem>客户使用行为影响系统安全或其他客户正常使用；</ListItem>
        <ListItem>第三方通信资源、号码资源或相关服务无法继续提供；</ListItem>
        <ListItem>法律法规、监管机构或平台规则要求停止服务。</ListItem>
      </UnorderedList>
      <P mt={3}>
        服务终止后，Bizsprite 可根据内部数据保留政策处理客户数据。客户如需导出数据，应在服务终止前提出申请。
      </P>
    </LegalSection>

    <LegalSection title="十五、宣传表述解释">
      <P mb={3}>Bizsprite 在网站、宣传资料、销售材料中提及的：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>固定月费；</ListItem>
        <ListItem>不按 AI Token 单独计费；</ListItem>
        <ListItem>不按 AI 使用量额外收费；</ListItem>
        <ListItem>套餐内 AI 来电接听；</ListItem>
        <ListItem>账单可控；</ListItem>
      </UnorderedList>
      <P mt={3}>
        均应理解为适用于正常商业客服场景下的套餐权益，并受本服务条款、合理使用政策及具体订单约定限制。
        上述表述不代表客户可以无限制、无条件、无成本地占用通信资源、AI 算力资源、系统资源或第三方服务资源。
      </P>
    </LegalSection>

    <LegalSection title="联系我们">
      <P>
        对本条款有任何疑问，请联系{' '}
        <Link href="mailto:support@bizsprite.com" color="blue.600">support@bizsprite.com</Link>，
        或致电 <Link href="tel:+14088779998" color="blue.600">+1 (408) 877-9998</Link>。
      </P>
    </LegalSection>
  </>
);

const Terms = () => {
  const [lang, setLangState] = useState(readLang);
  const setLang = (l) => {
    setLangState(l);
    writeLang(l);
  };
  const zh = lang === 'zh';

  return (
    <LegalShell
      lang={lang}
      setLang={setLang}
      title={zh ? 'Bizsprite AI 语音客服业务服务条款' : 'Terms and Conditions'}
      updated={zh ? '版本：V1.0（内部确认版）· 部分商业政策待确认' : undefined}
      notice={<TermsNotice lang={lang} />}
      footerExtra={
        <Link as={RouterLink} to="/privacy-policy" color="blue.500">
          {zh ? '隐私政策' : 'Privacy Policy'}
        </Link>
      }
    >
      {zh ? <ZhBody /> : <EnBody />}
    </LegalShell>
  );
};

export default Terms;
